import { getCssVar, getCssVarAsRgb } from './css-variables.js';

/**
 * Starfield Canvas Animation
 * Creates a rain of stars and comets before the video starts
 */
export class StarfieldCanvas {
    constructor(canvas, mode = 'default') {
        this.canvas = canvas;
        this.mode = mode;
        if (!this.canvas) {
            console.error('Starfield canvas not found!');
            return;
        }
        
        this.ctx = this.canvas.getContext('2d');
        if (!this.ctx) {
            console.error('Could not get 2D context');
            return;
        }
        
        this.stars = [];
        this.comets = [];
        this.animationId = null;
        this.isActive = false;
        this.transitionMode = false;
        this.transitionProgress = 0;
        this.videoCenter = { x: 0, y: 0 };
        this.isFullyInitialized = false;

        // Performance gating: only run when tab is visible AND the canvas is
        // on screen. Both start true; the observers correct them.
        this.tabVisible = (typeof document === 'undefined') || document.visibilityState !== 'hidden';
        this.inView = true;
        this.reducedMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;

        // Timer/observer handles so cleanup() can release them (no leaks).
        this.cometStartTimeout = null;
        this.cometInterval = null;
        this.viewportObserver = null;

        this.colors = this.getColors();

        // Bind event handlers to the instance
        this.resizeHandler = this.handleResize.bind(this);
        this.visibilityChangeHandler = this.handleVisibilityChange.bind(this);
        this.resizeTimeout = null;

        this.init();
    }

    getColors() {
        return {
            bg1: getCssVar('--canvas-starfield-bg-1'),
            bg2: getCssVar('--canvas-starfield-bg-2'),
            bg3: getCssVar('--canvas-starfield-bg-3'),
            star: getCssVar('--canvas-starfield-star-color'),
            cometHead: getCssVar('--canvas-starfield-comet-head'),
            cometTrail: getCssVarAsRgb('--canvas-starfield-comet-trail'),
            cometTrailGlow: getCssVarAsRgb('--canvas-starfield-comet-trail-glow'),
            cometCore: getCssVar('--canvas-starfield-comet-core'),
            starGlowCold: getCssVar('--canvas-starfield-star-glow-cold'),
            starGlowWarm: getCssVar('--canvas-starfield-star-glow-warm'),
            starGlowHot: getCssVar('--canvas-starfield-star-glow-hot'),
            starColorCold: getCssVar('--canvas-starfield-star-color-cold'),
            starColorWarm: getCssVar('--canvas-starfield-star-color-warm'),
            starColorHot: getCssVar('--canvas-starfield-star-color-hot'),
        };
    }
    
    init() {
        this.resizeCanvas();
        if (this.mode !== 'cometsOnly') {
            this.createStars();
        }
        this.createComets();
        this.setupEventListeners();
        this.setupViewportObserver();

        if (this.reducedMotion) {
            // Honour "reduce motion": paint one static frame, never loop.
            this.drawStaticFrame();
        } else {
            this.updateActive();
            this.startCometTimer();
        }
        this.isFullyInitialized = true;
    }

    // Single decision point: run only when tab is visible AND on screen.
    updateActive() {
        if (this.reducedMotion) return;
        const shouldRun = this.tabVisible && this.inView;
        if (shouldRun && !this.isActive) {
            this.isActive = true;
            this.animate();
        } else if (!shouldRun && this.isActive) {
            this.stopAnimation();
        }
    }

    setupViewportObserver() {
        if (!('IntersectionObserver' in window) || !this.canvas) return;
        this.viewportObserver = new IntersectionObserver((entries) => {
            this.inView = entries[0].isIntersecting;
            this.updateActive();
        }, { threshold: 0 });
        this.viewportObserver.observe(this.canvas);
    }

    drawStaticFrame() {
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        if (this.mode !== 'cometsOnly') {
            const gradient = this.ctx.createLinearGradient(0, 0, 0, this.canvas.height);
            gradient.addColorStop(0, this.colors.bg1);
            gradient.addColorStop(0.5, this.colors.bg2);
            gradient.addColorStop(1, this.colors.bg3);
            this.ctx.fillStyle = gradient;
            this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
            this.stars.forEach(star => star.draw(this.ctx));
        }
    }
    
    resizeCanvas() {
        this.canvas.width = window.innerWidth;
        this.canvas.height = window.innerHeight * 0.20; // 20% height for modern design
    }
    
    createStars() {
        this.stars = [];
        // Lower density + hard cap on phones to spare CPU/battery.
        const divisor = this.isMobile ? 7000 : 4000;
        let starCount = Math.floor((this.canvas.width * this.canvas.height) / divisor);
        if (this.isMobile) starCount = Math.min(starCount, 40);

        for (let i = 0; i < starCount; i++) {
            this.stars.push(new Star(this.canvas, this.colors));
        }
    }

    createComets() {
        this.comets = [];
        const cometCount = this.isMobile ? 2 : 3; // Fewer comets on phones
        for (let i = 0; i < cometCount; i++) {
            const comet = new Comet(this.canvas, this.colors);
            // Start some comets active immediately
            if (i < 2) {
                comet.active = true;
            }
            this.comets.push(comet);
        }
    }
    
    startCometTimer() {
        // Activate comets immediately
        this.cometStartTimeout = setTimeout(() => {
            if (this.isActive) {
                this.comets.forEach(comet => {
                    if (!comet.active) {
                        comet.active = true;
                    }
                });
            }
        }, 500); // Start after just 0.5 seconds

        // Then continue with regular intervals but much faster.
        // Handle stored so cleanup() can clear it — otherwise intervals pile
        // up on every theme switch and keep firing forever.
        this.cometInterval = setInterval(() => {
            if (this.isActive) {
                this.comets.forEach(comet => {
                    if (!comet.active) {
                        comet.active = true;
                    }
                });
            }
        }, 2000); // Every 2 seconds instead of 5
    }
    
    animate() {
        if (!this.isActive) return;
        
        this.updateTransition();
        
        this.ctx.clearRect(0, 0, this.canvas.width, this.canvas.height);
        
        // Only draw background in default mode
        if (this.mode !== 'cometsOnly') {
            const gradient = this.ctx.createLinearGradient(0, 0, 0, this.canvas.height);
            gradient.addColorStop(0, this.colors.bg1);
            gradient.addColorStop(0.5, this.colors.bg2);
            gradient.addColorStop(1, this.colors.bg3);
            this.ctx.fillStyle = gradient;
            this.ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);
        }
        
        // Only draw stars in default mode
        if (this.mode !== 'cometsOnly') {
            this.stars.forEach(star => {
                star.update(this.transitionMode ? this.transitionProgress : 0);
                star.draw(this.ctx);
            });
        }
        
        this.comets.forEach(comet => {
            comet.update(this.transitionMode ? this.transitionProgress : 0);
            comet.draw(this.ctx);
        });
        
        this.animationId = requestAnimationFrame(() => this.animate());
    }
    
    startAnimation() {
        this.animate();
    }
    
    stopAnimation() {
        this.isActive = false;
        if (this.animationId) {
            cancelAnimationFrame(this.animationId);
            this.animationId = null;
        }
    }
    
    cleanup() {
        this.stopAnimation();
        this.stars = [];
        this.comets = [];

        // Remove event listeners + timers + observer to prevent leaks.
        window.removeEventListener('resize', this.resizeHandler);
        document.removeEventListener('visibilitychange', this.visibilityChangeHandler);
        if (this.resizeTimeout) clearTimeout(this.resizeTimeout);
        if (this.cometStartTimeout) clearTimeout(this.cometStartTimeout);
        if (this.cometInterval) clearInterval(this.cometInterval);
        if (this.viewportObserver) {
            this.viewportObserver.disconnect();
            this.viewportObserver = null;
        }
    }
    
    setupEventListeners() {
        window.addEventListener('resize', this.resizeHandler);
        document.addEventListener('visibilitychange', this.visibilityChangeHandler);
        
        setTimeout(() => {
            const video = document.querySelector('.header-video');
            if (video) {
                video.addEventListener('canplay', () => {
                    this.enhanceForVideoStart();
                });
            }
        }, 500);
    }

    handleResize() {
        clearTimeout(this.resizeTimeout);
        this.resizeTimeout = setTimeout(() => {
            this.resizeCanvas();
            this.createStars();
            this.isMobile = window.matchMedia && window.matchMedia('(max-width: 768px)').matches;
            if (this.reducedMotion) this.drawStaticFrame();
        }, 250);
    }

    handleVisibilityChange() {
        // Tab hidden → pause; tab visible → resume only if also on screen.
        this.tabVisible = (document.visibilityState === 'visible');
        this.updateActive();
    }
    
    enhanceForVideoStart() {
        if (!this.isFullyInitialized || !this.stars.length || !this.comets.length) {
            setTimeout(() => {
                if (this.isFullyInitialized && this.stars.length && this.comets.length) {
                    this.enhanceForVideoStart();
                }
            }, 1000);
            return;
        }
        
        this.videoCenter.x = this.canvas.width / 2;
        this.videoCenter.y = this.canvas.height;
        
        this.transitionMode = true;
        this.transitionProgress = 0;
        
        if (this.comets.length < 4) {
            for (let i = this.comets.length; i < 4; i++) {
                const newComet = new Comet(this.canvas, this.colors);
                newComet.setTransitionTarget(this.videoCenter);
                this.comets.push(newComet);
            }
        }
        
        this.comets.forEach(comet => {
            if (!comet.active) {
                comet.active = true;
            }
            comet.setTransitionTarget(this.videoCenter);
        });
        
        this.stars.forEach(star => {
            star.setTransitionTarget(this.videoCenter);
        });
    }
    
    updateTransition() {
        if (!this.transitionMode) return;
        
        this.transitionProgress += 0.005;
        
        if (this.transitionProgress >= 1) {
            this.transitionProgress = 1;
        }
    }
}

class Star {
    constructor(canvas, colors) {
        this.x = Math.random() * canvas.width;
        this.y = Math.random() * canvas.height;
        this.size = Math.random() * 1.5 + 0.5;
        this.baseOpacity = Math.random() * 0.6 + 0.3;
        this.twinkleSpeed = Math.random() * 0.05 + 0.01;
        this.twinkleOffset = Math.random() * Math.PI * 2;
        this.time = 0;
        this.isBright = Math.random() < 0.1;
        this.pulseSpeed = this.isBright ? Math.random() * 0.02 + 0.01 : 0;
        this.temperature = Math.random();
        if (this.temperature < 0.3) {
            this.starType = 'cold';
        } else if (this.temperature < 0.7) {
            this.starType = 'warm';
        } else {
            this.starType = 'hot';
        }
        this.transitionTarget = null;
        this.originalX = this.x;
        this.originalY = this.y;
        this.colors = colors;
    }
    
    update(transitionProgress = 0) {
        this.time += this.twinkleSpeed;
        
        if (this.transitionTarget && transitionProgress > 0) {
            const easedProgress = this.easeInOutCubic(transitionProgress);
            this.x = this.originalX + (this.transitionTarget.x - this.originalX) * easedProgress;
            this.y = this.originalY + (this.transitionTarget.y - this.originalY) * easedProgress;
        }
    }
    
    setTransitionTarget(target) {
        this.transitionTarget = target;
        this.originalX = this.x;
        this.originalY = this.y;
    }
    
    easeInOutCubic(t) {
        return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
    }
    
    draw(ctx) {
        ctx.save();
        
        const twinkle = Math.sin(this.time + this.twinkleOffset) * 0.2;
        let currentOpacity = this.baseOpacity + twinkle;
        
        if (this.isBright) {
            const pulse = Math.sin(this.time * 2 + this.twinkleOffset) * 0.3;
            currentOpacity += pulse;
            
            ctx.shadowBlur = 6;
            if (this.starType === 'cold') {
                ctx.shadowColor = this.colors.starGlowCold;
            } else if (this.starType === 'warm') {
                ctx.shadowColor = this.colors.starGlowWarm;
            } else {
                ctx.shadowColor = this.colors.starGlowHot;
            }
        } else {
            ctx.shadowBlur = 0;
        }
        
        currentOpacity = Math.max(0.3, Math.min(1, currentOpacity));
        ctx.globalAlpha = currentOpacity;
        
        let starColor;
        if (this.starType === 'cold') {
            starColor = this.isBright ? this.colors.starColorCold : this.colors.starGlowCold;
        } else if (this.starType === 'warm') {
            starColor = this.isBright ? this.colors.starColorWarm : this.colors.starGlowWarm;
        } else {
            starColor = this.isBright ? this.colors.starColorHot : this.colors.starGlowHot;
        }
        ctx.fillStyle = starColor;
        
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.restore();
    }
}

class Comet {
    constructor(canvas, colors) {
        this.canvas = canvas;
        this.active = false;
        this.particles = [];
        this.colors = colors;
        this.reset();
    }
    
    reset() {
        const side = Math.random() < 0.5 ? 0 : this.canvas.width;
        this.x = side;
        this.y = Math.random() * this.canvas.height;
        this.size = Math.random() * 4 + 2;
        this.angle = Math.random() * 2 * Math.PI;
        this.speedX = Math.cos(this.angle) * (Math.random() * 5 + 4);
        this.speedY = Math.sin(this.angle) * (Math.random() * 5 + 4);
        this.active = false;
        this.trail = [];
        this.particles = [];
        this.time = 0;
        this.brightness = Math.random() * 0.5 + 0.5;
        this.transitionTarget = null;
        this.transitionMode = false;
    }
    
    update(transitionProgress = 0) {
        if (!this.active) return;
        
        this.time += 0.1;
        
        if (this.transitionTarget && transitionProgress > 0) {
            this.transitionMode = true;
            const targetDx = this.transitionTarget.x - this.x;
            const targetDy = this.transitionTarget.y - this.y;
            const distance = Math.sqrt(targetDx * targetDx + targetDy * targetDy);
            
            if (distance > 10) {
                const redirectStrength = transitionProgress * 0.1;
                this.speedX += (targetDx / distance) * redirectStrength;
                this.speedY += (targetDy / distance) * redirectStrength;
                
                const maxSpeed = 8;
                const currentSpeed = Math.sqrt(this.speedX * this.speedX + this.speedY * this.speedY);
                if (currentSpeed > maxSpeed) {
                    this.speedX = (this.speedX / currentSpeed) * maxSpeed;
                    this.speedY = (this.speedY / currentSpeed) * maxSpeed;
                }
            }
        }
        
        this.trail.push({
            x: this.x + Math.sin(this.time) * 0.5,
            y: this.y + Math.cos(this.time * 0.7) * 0.3,
            size: this.size * (0.8 + Math.sin(this.time * 2) * 0.2)
        });
        if (this.trail.length > 25) {
            this.trail.shift();
        }
        
        if (Math.random() < 0.7) {
            this.particles.push({
                x: this.x + (Math.random() - 0.5) * this.size,
                y: this.y + (Math.random() - 0.5) * this.size,
                vx: (Math.random() - 0.5) * 2 - this.speedX * 0.1,
                vy: (Math.random() - 0.5) * 2 - this.speedY * 0.1,
                life: 1,
                decay: Math.random() * 0.05 + 0.02,
                size: Math.random() * 2 + 0.5
            });
        }
        
        this.particles = this.particles.filter(particle => {
            particle.x += particle.vx;
            particle.y += particle.vy;
            particle.life -= particle.decay;
            particle.vx *= 0.98;
            particle.vy *= 0.98;
            return particle.life > 0;
        });
        
        this.x += this.speedX;
        this.y += this.speedY;
        
        if (this.x < -this.size * 2 || this.x > this.canvas.width + this.size * 2 || 
            this.y < -this.size * 2 || this.y > this.canvas.height + this.size * 2) {
            this.reset();
        }
    }
    
    draw(ctx) {
        if (!this.active) return;
        
        ctx.save();
        
        if (this.trail.length > 3) {
            ctx.globalCompositeOperation = 'lighter';
            
            for (let i = 1; i < this.trail.length - 1; i++) {
                const alpha = (i / this.trail.length) * 0.6;
                const current = this.trail[i];
                const next = this.trail[i + 1];
                
                ctx.globalAlpha = alpha * this.brightness;
                ctx.strokeStyle = `rgba(${this.colors.cometTrail}, ${alpha})`;
                ctx.lineWidth = current.size * alpha * 2;
                ctx.lineCap = 'round';
                
                ctx.beginPath();
                ctx.moveTo(current.x, current.y);
                ctx.lineTo(next.x, next.y);
                ctx.stroke();
            }
            
            ctx.globalAlpha = 0.3 * this.brightness;
            ctx.strokeStyle = `rgba(${this.colors.cometTrailGlow}, 0.3)`;
            ctx.lineWidth = this.size * 3;
            ctx.beginPath();
            ctx.moveTo(this.trail[0].x, this.trail[0].y);
            for (let i = 1; i < this.trail.length; i++) {
                ctx.lineTo(this.trail[i].x, this.trail[i].y);
            }
            ctx.stroke();
            
            ctx.globalCompositeOperation = 'source-over';
        }
        
        ctx.globalCompositeOperation = 'lighter';
        this.particles.forEach(particle => {
            ctx.globalAlpha = particle.life * 0.6;
            ctx.fillStyle = `rgba(${this.colors.cometTrailGlow}, ${particle.life})`;
            ctx.beginPath();
            ctx.arc(particle.x, particle.y, particle.size * particle.life, 0, Math.PI * 2);
            ctx.fill();
        });
        ctx.globalCompositeOperation = 'source-over';
        
        ctx.shadowBlur = 25;
        ctx.shadowColor = this.colors.cometHead;
        ctx.globalAlpha = this.brightness;
        ctx.fillStyle = this.colors.cometHead;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.shadowBlur = 10;
        ctx.fillStyle = this.colors.cometCore;
        ctx.beginPath();
        ctx.arc(this.x, this.y, this.size * 0.6, 0, Math.PI * 2);
        ctx.fill();
        
        ctx.restore();
    }
    
    setTransitionTarget(target) {
        this.transitionTarget = target;
    }
}